/**
 * ImportFormModal Component
 *
 * Modal for importing webforms from JSON files
 * Supports validation, error handling, and bulk import
 */

import React, { useState, useRef, useEffect } from 'react';
import { Modal, Button, Form, Alert, Spinner, Badge, ListGroup } from 'react-bootstrap';
import { MdUpload, MdClose, MdCheckCircle, MdError, MdWarning } from 'react-icons/md';
import toast from 'react-hot-toast';
import formExportImportService from '../../services/formExportImportService';
import dynamicFormsService from '../../services/dynamicFormsService';

const ImportFormModal = ({ show, onHide, onImportSuccess }) => {
    const [selectedFile, setSelectedFile] = useState(null);
    const [jsonData, setJsonData] = useState(null);
    const [validationResult, setValidationResult] = useState(null);
    const [isValidating, setIsValidating] = useState(false);
    const [isImporting, setIsImporting] = useState(false);
    const [importResult, setImportResult] = useState(null);
    const [departments, setDepartments] = useState([]);
    const [selectedDepartment, setSelectedDepartment] = useState('');
    const [needsDepartment, setNeedsDepartment] = useState(false);
    const [loadingDepartments, setLoadingDepartments] = useState(false);
    const fileInputRef = useRef(null);

    // Fetch departments on component mount
    useEffect(() => {
        if (show) {
            fetchDepartments();
        }
    }, [show]);

    const fetchDepartments = async () => {
        setLoadingDepartments(true);
        try {
            const response = await dynamicFormsService.getDepartments();
            console.log('Departments response:', response); // Debug log
            if (response.success && response.data) {
                // response.data is already the array of departments
                const deptArray = Array.isArray(response.data) ? response.data : [];
                setDepartments(deptArray);
                console.log(`Loaded ${deptArray.length} departments`);
            } else {
                console.warn('Failed to fetch departments:', response);
                setDepartments([]);
            }
        } catch (error) {
            console.error('Error fetching departments:', error);
            toast.error('Failed to load departments');
            setDepartments([]);
        } finally {
            setLoadingDepartments(false);
        }
    };

    const checkIfNeedsDepartment = (data) => {
        const forms = Array.isArray(data) ? data : [data];

        // Check if any form needs a department
        return forms.some(form => {
            const hasDepartmentId = form.department_id && form.department_id !== 'UNASSIGNED_DEPARTMENT';
            const hasAllowedDepartments = form.allowed_departments && form.allowed_departments.length > 0;
            return !hasDepartmentId && !hasAllowedDepartments;
        });
    };

    const handleFileSelect = async (event) => {
        const file = event.target.files[0];
        if (!file) return;

        if (!file.name.endsWith('.json')) {
            toast.error('Please select a valid JSON file');
            return;
        }

        setSelectedFile(file);
        setValidationResult(null);
        setImportResult(null);
        setIsValidating(true);

        try {
            // Read and parse the JSON file
            const result = await formExportImportService.readJSONFile(file);

            if (result.success) {
                setJsonData(result.data);

                // Check if form needs a department
                const needsDept = checkIfNeedsDepartment(result.data);
                setNeedsDepartment(needsDept);

                // Validate the JSON structure
                const validation = formExportImportService.validateFormJSON(result.data);
                setValidationResult(validation);

                if (!validation.valid) {
                    toast.error('Invalid form data format');
                }
            } else {
                toast.error(result.error || 'Failed to read file');
                setSelectedFile(null);
            }
        } catch (error) {
            console.error('File read error:', error);
            toast.error(error.error || 'Failed to read file');
            setSelectedFile(null);
        } finally {
            setIsValidating(false);
        }
    };

    const handleImport = async () => {
        if (!jsonData || !validationResult?.valid) {
            toast.error('Please select a valid form file first');
            return;
        }

        // Check if department is required but not selected
        if (needsDepartment && !selectedDepartment) {
            toast.error('Please select a default department for the form(s)');
            return;
        }

        setIsImporting(true);
        setImportResult(null);

        try {
            const forms = Array.isArray(jsonData) ? jsonData : [jsonData];
            let result;

            if (forms.length === 1) {
                // Single form import
                result = await formExportImportService.importForm(
                    forms[0],
                    false,
                    false,
                    selectedDepartment || null
                );
            } else {
                // Bulk import
                result = await formExportImportService.importFormsBulk(
                    forms,
                    false,
                    false,
                    selectedDepartment || null
                );
            }

            if (result.success) {
                setImportResult(result.data);

                const successCount = result.data.success_count || 0;
                const failedCount = result.data.failed_count || 0;

                if (successCount > 0) {
                    toast.success(
                        `Successfully imported ${successCount} form${successCount > 1 ? 's' : ''}`
                    );

                    // Call the success callback first to refresh the form list
                    console.log('✅ Import successful, triggering refresh callback');

                    // Show refreshing message
                    toast.loading('Refreshing page...', { duration: 2000 });

                    // Reset form state before callback
                    setSelectedFile(null);
                    setJsonData(null);
                    setValidationResult(null);
                    setImportResult(null);
                    setSelectedDepartment('');
                    setNeedsDepartment(false);
                    setLoadingDepartments(false);
                    if (fileInputRef.current) {
                        fileInputRef.current.value = '';
                    }

                    // Trigger parent callback to refresh list and close modal
                    // Parent will handle closing the modal and reloading the page
                    if (onImportSuccess) {
                        await onImportSuccess();
                    }
                } else {
                    toast.error('Failed to import forms');
                }
            } else {
                toast.error(result.error || 'Failed to import forms');
            }
        } catch (error) {
            console.error('Import error:', error);
            toast.error('An error occurred during import');
        } finally {
            setIsImporting(false);
        }
    };

    const handleClose = () => {
        setSelectedFile(null);
        setJsonData(null);
        setValidationResult(null);
        setImportResult(null);
        setSelectedDepartment('');
        setNeedsDepartment(false);
        setLoadingDepartments(false);
        if (fileInputRef.current) {
            fileInputRef.current.value = '';
        }
        onHide();
    };

    const renderValidationResult = () => {
        if (!validationResult) return null;

        return (
            <div className="mb-3">
                {validationResult.valid ? (
                    <Alert variant="success">
                        <MdCheckCircle className="me-2" />
                        <strong>Validation Passed!</strong> Found {validationResult.formsCount}{' '}
                        valid form{validationResult.formsCount > 1 ? 's' : ''} in the file.
                    </Alert>
                ) : (
                    <Alert variant="danger">
                        <MdError className="me-2" />
                        <strong>Validation Failed</strong>
                    </Alert>
                )}

                {validationResult.errors?.length > 0 && (
                    <Alert variant="danger">
                        <strong>Errors:</strong>
                        <ul className="mb-0 mt-2">
                            {validationResult.errors.map((error, index) => (
                                <li key={index}>{error}</li>
                            ))}
                        </ul>
                    </Alert>
                )}

                {validationResult.warnings?.length > 0 && (
                    <Alert variant="warning">
                        <MdWarning className="me-2" />
                        <strong>Warnings:</strong>
                        <ul className="mb-0 mt-2">
                            {validationResult.warnings.map((warning, index) => (
                                <li key={index}>{warning}</li>
                            ))}
                        </ul>
                    </Alert>
                )}
            </div>
        );
    };

    const renderImportResult = () => {
        if (!importResult) return null;

        const { imported_forms, success_count, failed_count, total_count } = importResult;

        return (
            <Alert variant={success_count > 0 ? 'success' : 'danger'} className="mt-3">
                <strong>Import Results:</strong>
                <div className="mt-2">
                    <div>Total: {total_count}</div>
                    <div className="text-success">Success: {success_count}</div>
                    <div className="text-danger">Failed: {failed_count}</div>
                </div>

                {imported_forms && imported_forms.length > 0 && (
                    <ListGroup className="mt-3">
                        {imported_forms.map((form, index) => (
                            <ListGroup.Item
                                key={index}
                                variant={form.status === 'success' ? 'success' : 'danger'}
                            >
                                <div className="d-flex justify-content-between align-items-start">
                                    <div>
                                        <strong>{form.form_name}</strong>
                                        {form.new_form_id && (
                                            <div className="text-muted small">
                                                New ID: {form.new_form_id}
                                            </div>
                                        )}
                                        {form.error && (
                                            <div className="text-danger small">{form.error}</div>
                                        )}
                                    </div>
                                    <Badge bg={form.status === 'success' ? 'success' : 'danger'}>
                                        {form.status}
                                    </Badge>
                                </div>
                                {form.warnings && form.warnings.length > 0 && (
                                    <div className="mt-2">
                                        <small className="text-warning">
                                            Warnings:
                                            {form.warnings.map((w, i) => (
                                                <div key={i}>- {w.message || w}</div>
                                            ))}
                                        </small>
                                    </div>
                                )}
                            </ListGroup.Item>
                        ))}
                    </ListGroup>
                )}
            </Alert>
        );
    };

    return (
        <Modal show={show} onHide={handleClose} size="lg" centered>
            <Modal.Header closeButton>
                <Modal.Title>
                    <MdUpload className="me-2" />
                    Import Form from JSON
                </Modal.Title>
            </Modal.Header>

            <Modal.Body>
                <Alert variant="info">
                    <strong>Import Process:</strong>
                    <ol className="mb-0 mt-2">
                        <li>Select a JSON file containing form data</li>
                        <li>The system will validate the form structure</li>
                        <li>A new form with a new ID will be created</li>
                        <li>Roles and departments will be validated</li>
                    </ol>
                </Alert>

                <Form.Group className="mb-3">
                    <Form.Label>Select JSON File</Form.Label>
                    <Form.Control
                        ref={fileInputRef}
                        type="file"
                        accept=".json"
                        onChange={handleFileSelect}
                        disabled={isImporting}
                    />
                    <Form.Text className="text-muted">
                        Select a JSON file exported from this system
                    </Form.Text>
                </Form.Group>

                {needsDepartment && (
                    <Form.Group className="mb-3">
                        <Form.Label>
                            Default Department <span className="text-danger">*</span>
                        </Form.Label>
                        <Form.Select
                            value={selectedDepartment}
                            onChange={(e) => setSelectedDepartment(e.target.value)}
                            disabled={isImporting || loadingDepartments}
                        >
                            {loadingDepartments ? (
                                <option value="">Loading departments...</option>
                            ) : departments.length === 0 ? (
                                <option value="">No departments available</option>
                            ) : (
                                <>
                                    <option value="">Select a department...</option>
                                    {departments.map((dept) => (
                                        <option key={dept.department_id} value={dept.department_id}>
                                            {dept.name}
                                        </option>
                                    ))}
                                </>
                            )}
                        </Form.Select>
                        <Form.Text className="text-muted">
                            The imported form(s) do not have a department assigned. Please select a default department.
                        </Form.Text>
                    </Form.Group>
                )}

                {isValidating && (
                    <div className="text-center py-3">
                        <Spinner animation="border" role="status" />
                        <div className="mt-2">Validating file...</div>
                    </div>
                )}

                {renderValidationResult()}
                {renderImportResult()}

                {selectedFile && !isValidating && (
                    <Alert variant="secondary">
                        <strong>Selected file:</strong> {selectedFile.name}
                        <div className="text-muted small">
                            Size: {(selectedFile.size / 1024).toFixed(2)} KB
                        </div>
                    </Alert>
                )}
            </Modal.Body>

            <Modal.Footer>
                <Button variant="secondary" onClick={handleClose} disabled={isImporting}>
                    <MdClose className="me-1" />
                    {importResult ? 'Close' : 'Cancel'}
                </Button>
                {!importResult && (
                    <Button
                        variant="primary"
                        onClick={handleImport}
                        disabled={
                            isImporting ||
                            !validationResult?.valid ||
                            !selectedFile ||
                            (needsDepartment && !selectedDepartment)
                        }
                    >
                        {isImporting ? (
                            <>
                                <Spinner
                                    as="span"
                                    animation="border"
                                    size="sm"
                                    role="status"
                                    aria-hidden="true"
                                    className="me-2"
                                />
                                Importing...
                            </>
                        ) : (
                            <>
                                <MdUpload className="me-1" />
                                Import Form{validationResult?.formsCount > 1 ? 's' : ''}
                            </>
                        )}
                    </Button>
                )}
            </Modal.Footer>
        </Modal>
    );
};

export default ImportFormModal;
